import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Filter, X, Star, TrendingUp, DollarSign, Grid3x3, List, Eye } from 'lucide-react';
import { ProductCard } from '../components/product/ProductCard';
import { CategoryChip } from '../components/product/CategoryChip';
import { QuickViewModal } from '../components/product/QuickViewModal';
import { Pagination } from '../components/common/Pagination';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useProducts, useCategories, useSubcategories } from '../hooks/useData';

export const Shop = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [minRating, setMinRating] = useState(0);
  const [minDiscount, setMinDiscount] = useState(0);
  const itemsPerPage = 12;

  const { categories } = useCategories();
  const { subcategories } = useSubcategories(selectedCategory);
  const { products, loading } = useProducts({
    category_id: selectedCategory,
    subcategory_id: selectedSubcategory,
    search: searchQuery
  });

  // Get unique brands from products
  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))];

  // Calculate products count per category
  const getCategoryProductCount = (categoryId) => {
    return products.filter(p => p.category_id === categoryId).length;
  };

  // Filter and sort products
  const filteredProducts = products.filter(product => {
    const price = product.discount > 0
      ? product.price - (product.price * product.discount / 100)
      : product.price;

    if (priceRange.min && price < parseFloat(priceRange.min)) return false;
    if (priceRange.max && price > parseFloat(priceRange.max)) return false;
    if (selectedBrands.length > 0 && !selectedBrands.includes(product.brand)) return false;
    if (minRating > 0 && (product.rating || 0) < minRating) return false;
    if (minDiscount > 0 && (product.discount || 0) < minDiscount) return false;
    return true;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
        const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
        return priceA - priceB;
      case 'price-high':
        const priceA2 = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
        const priceB2 = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
        return priceB2 - priceA2;
      case 'top-rated':
        return (b.rating || 0) - (a.rating || 0);
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  // Get top rated products
  const topRatedProducts = [...products]
    .filter(p => p.rating && p.rating >= 4.5)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0));

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSearchQuery('');
    setPriceRange({ min: '', max: '' });
    setSelectedBrands([]);
    setMinRating(0);
    setMinDiscount(0);
    setCurrentPage(1);
    setSearchParams({});
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const hasActiveFilters = selectedCategory || selectedSubcategory || searchQuery || 
                          priceRange.min || priceRange.max || selectedBrands.length > 0 || 
                          minRating > 0 || minDiscount > 0;

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-4">
            {t('shop.title')}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t('shop.subtitle')}
          </p>
        </motion.div>

        {/* Horizontal Category Carousel */}
        <div className="mb-8 -mx-4 sm:mx-0">
          <div className="flex items-center justify-between mb-4 px-4 sm:px-0">
            <h2 className="font-serif text-2xl font-semibold">{t('shop.shopByCategory')}</h2>
          </div>
          <div className="relative">
            <div className="flex gap-4 overflow-x-auto pb-4 px-4 sm:px-0 scrollbar-hide"
                 style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {categories.map((category) => (
                <CategoryChip
                  key={category.id}
                  category={category}
                  productsCount={getCategoryProductCount(category.id)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Enhanced Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              {/* Mobile Filter Toggle */}
              <Button
                variant="outline"
                className="w-full lg:hidden mb-4"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide' : 'Show'} Filters
                {hasActiveFilters && (
                  <Badge variant="default" className="ml-2">{
                    [selectedCategory, selectedSubcategory, searchQuery, priceRange.min, priceRange.max, ...selectedBrands]
                      .filter(Boolean).length
                  }</Badge>
                )}
              </Button>

              {/* Filters */}
              <div className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-xl font-semibold">Filters</h2>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear All
                    </Button>
                  )}
                </div>

                {/* Search */}
                <div>
                  <Label className="mb-2 block font-semibold">Search</Label>
                  <Input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Category */}
                <div>
                  <Label className="mb-2 block font-semibold">Category</Label>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setSelectedCategory('');
                        setSelectedSubcategory('');
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-smooth ${
                        selectedCategory === ''
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      All Categories
                    </button>
                    {categories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setSelectedSubcategory('');
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-smooth ${
                          selectedCategory === category.id
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subcategory */}
                {selectedCategory && subcategories.length > 0 && (
                  <div>
                    <Label className="mb-2 block font-semibold">Subcategory</Label>
                    <div className="space-y-2">
                      <button
                        onClick={() => setSelectedSubcategory('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-smooth ${
                          selectedSubcategory === ''
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        All Subcategories
                      </button>
                      {subcategories.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => setSelectedSubcategory(sub.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-smooth ${
                            selectedSubcategory === sub.id
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                        >
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Range */}
                <div>
                  <Label className="mb-2 block font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Price Range
                  </Label>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      placeholder="Min price"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                    />
                    <Input
                      type="number"
                      placeholder="Max price"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                    />
                  </div>
                  {/* Quick Price Filters */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPriceRange({ min: '', max: '20' })}
                      className="text-xs"
                    >
                      Under €20
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPriceRange({ min: '20', max: '40' })}
                      className="text-xs"
                    >
                      €20 - €40
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPriceRange({ min: '40', max: '' })}
                      className="text-xs"
                    >
                      Over €40
                    </Button>
                  </div>
                </div>

                {/* Rating Filter */}
                <div>
                  <Label className="mb-2 block font-semibold flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Minimum Rating
                  </Label>
                  <div className="space-y-2">
                    {[4, 3, 2, 1].map(rating => (
                      <button
                        key={rating}
                        onClick={() => setMinRating(minRating === rating ? 0 : rating)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-smooth flex items-center gap-2 ${
                          minRating === rating
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          {[...Array(rating)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </div>
                        <span>& Up</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Discount Filter */}
                <div>
                  <Label className="mb-2 block font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Minimum Discount
                  </Label>
                  <div className="space-y-2">
                    {[20, 15, 10, 5].map(discount => (
                      <button
                        key={discount}
                        onClick={() => setMinDiscount(minDiscount === discount ? 0 : discount)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-smooth ${
                          minDiscount === discount
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {discount}% Off or More
                      </button>
                    ))}
                  </div>
                </div>

                {/* Brand Filter */}
                {brands.length > 0 && (
                  <div>
                    <Label className="mb-2 block font-semibold">Brands</Label>
                    <div className="space-y-2">
                      {brands.slice(0, 8).map(brand => (
                        <label
                          key={brand}
                          className="flex items-center space-x-2 cursor-pointer hover:bg-muted px-2 py-1 rounded transition-smooth"
                        >
                          <input
                            type="checkbox"
                            checked={selectedBrands.includes(brand)}
                            onChange={() => toggleBrand(brand)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm">{brand}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Rated */}
                {topRatedProducts.length > 0 && (
                  <div>
                    <Label className="mb-2 block font-semibold flex items-center gap-2">
                      <Star className="w-4 h-4 fill-primary text-primary" />
                      Top Rated
                    </Label>
                    <div className="space-y-3">
                      {topRatedProducts.slice(0, 3).map(product => (
                        <Link
                          key={product.id}
                          to={`/product/${product.id}`}
                          className="flex gap-3 p-2 rounded-lg hover:bg-muted transition-smooth group"
                        >
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            {product.images && product.images[0] ? (
                              <img
                                src={product.images[0]}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-xs text-muted-foreground">NC</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-smooth">
                              {product.name}
                            </p>
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3 h-3 fill-primary text-primary" />
                              <span className="text-xs font-semibold">{product.rating}</span>
                              <span className="text-xs text-muted-foreground">({product.reviews_count})</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Filters */}
                <div>
                  <Label className="mb-2 block font-semibold">Quick Filters</Label>
                  <div className="space-y-2">
                    <Button
                      variant={sortBy === 'top-rated' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setSortBy('top-rated')}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Top Rated
                    </Button>
                    <Button
                      variant={sortBy === 'newest' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setSortBy('newest')}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Newest Arrivals
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {/* Sort and Count */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <p className="text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedProducts.length)} of {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-4">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 border border-input rounded-lg p-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8 w-8 p-0"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="h-8 w-8 p-0"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Sort Dropdown */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-input bg-background text-sm"
                  >
                    <option value="featured">Featured</option>
                    <option value="newest">Newest</option>
                    <option value="top-rated">Top Rated</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : sortedProducts.length > 0 ? (
              <>
                <div className={viewMode === 'grid' 
                  ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" 
                  : "flex flex-col gap-4"
                }>
                  {paginatedProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <ProductCard 
                        product={product} 
                        viewMode={viewMode}
                        onQuickView={() => setQuickViewProduct(product)}
                      />
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-12">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="font-serif text-2xl font-semibold mb-2">No products found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters or search query
                </p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            )}
          </div>
        </div>

        {/* Quick View Modal */}
        <QuickViewModal
          product={quickViewProduct}
          isOpen={!!quickViewProduct}
          onClose={() => setQuickViewProduct(null)}
        />
      </div>
    </div>
  );
};